import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import StepperProvider, { useStepper } from 'components/stepper/stepper-provider';
import type { Steps } from 'components/stepper/stepper-models';
import { CreateApiBasicInfoForm } from './forms/create-api-basic-info-form';
import { CreateApiInputParametersForm } from './forms/create-api-input-parameters-form';
import { CreateApiOutputParametersForm } from './forms/create-api-output-parameters-form';
import { CreateApiDebuggingForm } from './forms/create-api-debugging-form';
import { CreateApiProvider, useCreateApiContext, type IParameter } from './create-api-context';
import { useGetToolById } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { getToolApiConfig, normalizeToolDetails } from '../components/tool-detail-utils';
import { CreateApiSidebar } from './create-api-sidebar';

export function CreateAPIWrapper() {
  const { t } = useTranslation();
  const { toolId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const initialStep = Number(searchParams.get('step')) || 1;
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';

  const stepData: Steps = useMemo(
    () => [
      { id: 1, title: t('BASIC_INFORMATION') || 'Basic Information' },
      { id: 2, title: t('INPUT_PARAMETERS') || 'Input Parameters' },
      { id: 3, title: t('OUTPUT_PARAMETERS') || 'Output Parameters' },
      { id: 4, title: t('DEBUGGING') || 'Debugging' },
    ],
    [t]
  );

  return (
    <CreateApiProvider>
      <StepperProvider steps={stepData} initialStep={initialStep >= 1 && initialStep <= 4 ? initialStep : 1}>
        <CreateAPI toolId={toolId} isBlocksTool={isBlocksTool} />
      </StepperProvider>
    </CreateApiProvider>
  );
}

function CreateApiStepContent({ toolId }: Readonly<{ toolId: string }>) {
  const { currentStep } = useStepper();
  const [searchParams] = useSearchParams();
  const actionId = searchParams.get('actionId');

  switch (currentStep) {
    case 1:
      return <CreateApiBasicInfoForm key={actionId || 'new'} toolId={toolId} />;
    case 2:
      return <CreateApiInputParametersForm />;
    case 3:
      return <CreateApiOutputParametersForm toolId={toolId} />;
    case 4:
      return <CreateApiDebuggingForm toolId={toolId} />;
    default:
      return null;
  }
}

function CreateAPI({ toolId, isBlocksTool }: Readonly<{ toolId: string; isBlocksTool: boolean }>) {
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const [searchParams] = useSearchParams();
  const actionId = searchParams.get('actionId');
  const { setFormData } = useCreateApiContext();
  const toolQuery = useGetToolById({ toolId, projectKey }, { enabled: !!toolId && !!projectKey });
  const tool = useMemo(() => normalizeToolDetails(toolQuery.data), [toolQuery.data]);
  const toolApiConfig = getToolApiConfig(tool);
  const [isDataReady, setIsDataReady] = useState(false);
  const hydratedActionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (actionId) {
      if (!toolQuery.isSuccess || !toolApiConfig?.Actions) {
        return;
      }

      const action = toolApiConfig.Actions[actionId];
      const hydrationKey = `${actionId}:${toolId}`;

      if (action && hydratedActionKeyRef.current !== hydrationKey) {
        setFormData({
          actionId,
          name: action.Name || '',
          description: action.Description || '',
          method: action.Method || 'GET',
          endpoint: action.Endpoint || '',
          input_parameters: (action.InputParameters as Record<string, IParameter>) || {},
          output_parameters: (action.OutputParameters as Record<string, IParameter>) || {},
          graphql_query: action.GraphQLQuery || '',
          graphql_operation_type: action.GraphQLOperationType,
        });
        hydratedActionKeyRef.current = hydrationKey;
      }

      setIsDataReady(true);
      return;
    }

    hydratedActionKeyRef.current = null;
    setIsDataReady(true);
  }, [actionId, setFormData, toolApiConfig, toolId, toolQuery.isSuccess]);

  if (!isDataReady) {
    return null;
  }

  return (
    <div className="flex min-h-screen w-full flex-col lg:flex-row">
      <CreateApiSidebar toolId={toolId} isBlocksTool={isBlocksTool} />

      <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-10 xl:px-10 xl:pt-24">
        <div className="mx-auto w-full max-w-4xl lg:max-w-none">
          <CreateApiStepContent toolId={toolId} />
        </div>
      </main>
    </div>
  );
}

export default CreateAPIWrapper;
